import { useMemo } from "react";
import { PageLayout } from "../../layouts";
import SpeedControlProvider, { Thresholds } from "../../context/SpeedControl";
import Header from "./Header";
import { EKartRacingStatus, useSpeedControl } from "../../context/SpeedControl";
import LoadingSpinner from "../../components/Shared/LoadingSpinner";
import DataTable from "../../components/Shared/DataTable";
import { useSession } from "../../components/Session";

export const SpeedControl = () => {
  return (
    <SpeedControlProvider>
      <SpeedControlPage />
    </SpeedControlProvider>
  );
};

const SpeedControlPage = () => {
  const { permissions } = useSession();

  const hasManageScriptPermission = useMemo(
    () => permissions?.features?.includes("SPEED_CONTROL_SCRIPT_MANAGE"),
    [permissions]
  );

  return (
    <PageLayout Header={hasManageScriptPermission ? <Header /> : undefined } >
      <ThresholdsTable />
      <KartDataTable />
      <SpeedChangeLogsTable />
    </PageLayout>
  );
};

// Takes in a bestTime like 72281 and returns 1:12.281, or 52142 and returns 52.142
const formatBestTime = (bestTime: number) => {
  if (!bestTime) return '-';

  const minutes = Math.floor(bestTime / 60000);
  const seconds = Math.floor((bestTime % 60000) / 1000);
  const milliseconds = bestTime % 1000;

  if (minutes === 0) {
    return `${seconds}.${milliseconds.toString().padStart(3, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
};

const Title = ({ title }: { title: string }) => {
  return (
    <div className="flex items-center justify-center mx-4 px-10">
      <h2 className="text-2xl font-bold">{title}</h2>
    </div>
  );
};

const KartDataTable = () => {
  const { isLoading, currentKarts } = useSpeedControl();
  const filteredCurrentKarts = useMemo(
    () => {
      const filteredKarts = currentKarts.filter(kart => kart.calculatedData.status !== EKartRacingStatus.offTrack);
      const sortedKarts = filteredKarts.sort((a, b) => {
        const timeA = a.racefacerData?.best_time_raw || Number.MAX_SAFE_INTEGER;
        const timeB = b.racefacerData?.best_time_raw || Number.MAX_SAFE_INTEGER;
        return timeA - timeB;
      });
      return sortedKarts.map(kart => {
        return [
          kart.kart.toString(),
          formatBestTime(kart.racefacerData?.best_time_raw || 0),
          formatBestTime(kart.racefacerData?.last_time_raw || 0),
          kart.calculatedData.new_display,
          kart.calculatedData.eval_result,
        ];
      });
    },
    [currentKarts]
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const headers = ["Kart", "Best Time", "Last Time", "Speed", "Eval Result"];

  return <DataTable headers={headers} data={filteredCurrentKarts} title={<Title title="Kart Data" />} />
};

const ThresholdsTitle = ({ trackName }: { trackName: string }) => {
  return (
    <div className="flex justify-between items-center w-[100%] pr-10">
      <Title title="Thresholds" />
      <h2 className="text-lg font-medium">{trackName}</h2>
    </div>
  );
}

const DEFAULT_THRESHOLDS: Thresholds = {
  trackName: "Unknown",
  s1: [{speedset: 0, eligibility: 0}],
  s2: [{speedset: 0, eligibility: 0}],
  s3: [{speedset: 0, eligibility: 0}],
  lap: [{speedset: 0, eligibility: 0}],
}

const ThresholdsTable = () => {
  const { isLoading, thresholds } = useSpeedControl();

  const chosenThreshold = useMemo(() => {
    let trackIds = Object.keys(thresholds.trackToThreshold);

    if (!trackIds.length) {
      return DEFAULT_THRESHOLDS;
    }

    if (trackIds.length > 1) {
      trackIds = trackIds.filter(id => id !== "0");
    }

    // If no valid tracks remain after filtering, return default
    if (!trackIds.length) {
      return DEFAULT_THRESHOLDS;
    }

    // Take the last track ID (most recent)
    const chosenTrackId = parseInt(trackIds[trackIds.length - 1]);
    
    // If the track ID is invalid or not found, return default
    if (isNaN(chosenTrackId) || !thresholds.trackToThreshold[chosenTrackId]) {
      return DEFAULT_THRESHOLDS;
    }

    return thresholds.trackToThreshold[chosenTrackId];
  }, [thresholds]);

  const thresholdData = useMemo(() => {    
    if (!chosenThreshold) return [];

    const getSpeedsetString = (speedset: number, index: number) => {
      // Index 0 = 25%, increases by 5% from that
      const speed = 25 + index * 5;
      return `${speed}% (${speedset})`;
    }
    // Remove first threshold from display since it has very large

    const processedData = chosenThreshold.s1.map((_, index) => [
      getSpeedsetString(chosenThreshold.s1[index].speedset, index),
      chosenThreshold.s1[index].eligibility.toFixed(3),
      chosenThreshold.s2[index].eligibility.toFixed(3),
      chosenThreshold.s3[index].eligibility.toFixed(3),
      chosenThreshold.lap[index].eligibility.toFixed(3)
    ]);

    processedData.reverse(); // 100% -> 25%
    processedData.pop(); // Remove 25% threshold from display since values are very large (e.g. s1 = 15729.000)
    return processedData;
  }, [chosenThreshold]);

  const headers = ["Speedset", "S1", "S2", "S3", "Lap"];

  return (isLoading ? <LoadingSpinner /> : <DataTable headers={headers} data={thresholdData} title={<ThresholdsTitle trackName={chosenThreshold.trackName}/>} />);
};

const SpeedChangeLogsTable = () => {
  const { isLoading, speedChangeLogs } = useSpeedControl();

  const formattedLogs = useMemo(() => {
    return speedChangeLogs
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .map(log => [
        log.kart.toString(),
        new Date(log.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }),
        `${log.oldSpeedsetValue}% (${log.oldSpeedsetId})`,
        `${log.newSpeedsetValue}% (${log.newSpeedsetId})`,
        log.reason ?? "-"
      ]);
  }, [speedChangeLogs]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const headers = ["Kart", "Time", "Old Speedset", "New Speedset", "Reason"];

  return <DataTable title={<Title title="Speed change logs" />} headers={headers} data={formattedLogs}/>
};

